import { DESIGN_PALETTE, DESIGN_SIZE } from '@kopi-kaki/protocol';
import type { Direction, DrinkKind, Look } from '@kopi-kaki/protocol';
import { PathBuilder, PixelCanvas, between, mix, shade } from './pixel.js';
import type { Gradient, Point } from './pixel.js';
import {
  BOTTOM_STYLES,
  DRINK_COLORS,
  EYE_COLOR,
  EYE_STYLES,
  GLASSES_STYLES,
  HAT_STYLES,
  OUTLINE_COLOR,
  PROP_STYLES,
  SHOE_COLOR,
  SHOE_STYLES,
  TOP_STYLES,
  bottomColorOf,
  designPixels,
  hairOf,
  lookKey,
  shoesColorOf,
  skinOf,
  styleIndex,
  topColorOf,
} from './palette.js';
import type { HairOption } from './palette.js';

/**
 * Vector-style avatars.
 *
 * Each frame is built from smooth shapes -- rounded bodies, capsule limbs,
 * curved hair -- on the same 32x64 canvas the game has always used, so the
 * anchors, seat row and hit boxes are unchanged. Five views cover the eight
 * facings (the other three are mirror images), and every view is authored
 * facing right.
 *
 * Clothing styles are layered on one shared body, the way they always were:
 * legs first, then the bottom and shoe styles, then the torso with its
 * per-style details, the arms, and finally head, hair, glasses and hat.
 */

export const AVATAR_WIDTH = 32;
export const AVATAR_HEIGHT = 64;
/** Canvas row just below the feet; the sprite's anchor sits here. */
export const AVATAR_BASELINE = 60;
/** Canvas row of the hips when sitting, which the renderer lines up with the seat. */
export const AVATAR_SEAT_ROW = 47;
/** Canvas row where a swimming/treading avatar is cut off: everything below is underwater. */
export const AVATAR_WATERLINE = 37;

export type AvatarView = 'front' | 'front3q' | 'side' | 'back3q' | 'back';
export type AvatarAction = 'stand' | 'walk' | 'sit' | 'wave' | 'dance' | 'swim' | 'treadWater';

export interface AvatarPose {
  direction: Direction;
  action: AvatarAction;
  /** Animation frame; each action wraps it as needed. */
  frame: number;
  carrying: DrinkKind | null;
  /** Raise the drink to the mouth. */
  sipping: boolean;
  /** On a phone call: a phone held up to the ear. */
  phone?: boolean;
  blinking: boolean;
}

/** Which drawn view, and whether it is mirrored, represents each facing. */
const VIEW_FOR_DIRECTION: ReadonlyArray<{ view: AvatarView; mirror: boolean }> = [
  { view: 'back3q', mirror: false }, // 0 north: screen up-right
  { view: 'side', mirror: false }, // 1 north-east: screen right
  { view: 'front3q', mirror: false }, // 2 east: screen down-right
  { view: 'front', mirror: false }, // 3 south-east: screen down
  { view: 'front3q', mirror: true }, // 4 south: screen down-left
  { view: 'side', mirror: true }, // 5 south-west: screen left
  { view: 'back3q', mirror: true }, // 6 west: screen up-left
  { view: 'back', mirror: false }, // 7 north-west: screen up
];

export function viewFor(direction: Direction): { view: AvatarView; mirror: boolean } {
  return VIEW_FOR_DIRECTION[direction] ?? VIEW_FOR_DIRECTION[3]!;
}

/** Style indices, resolved against the tables in palette.ts. */
const TOP = { tee: 0, singlet: 1, polo: 2, office: 3, hoodie: 4, kebaya: 5, jersey: 6, apron: 7, batik: 8, varsity: 9, turtleneck: 10 } as const;
const BOTTOM = { trousers: 0, shorts: 1, skirt: 2, sarong: 3, cargo: 4, overalls: 5 } as const;
const SHOES = { sneakers: 0, slippers: 1, boots: 2, sandals: 3, loafers: 4, heels: 5 } as const;
const HAT = { none: 0, cap: 1, beanie: 2, straw: 3, chef: 4, hardHat: 5, headphones: 6, flower: 7, party: 8, topHat: 9, crown: 10, beret: 11, bandana: 12, firecracker: 13 } as const;
const GLASSES = { none: 0, round: 1, shades: 2, nerd: 3, hearts: 4, catEye: 5, monocle: 6, diamond: 7 } as const;
const PROP = { none: 0, red: 1, blue: 2, green: 3, purple: 4, pink: 5, white: 6 } as const;

/** Blade colours for the held lightsaber props: a bright core and a soft outer glow. */
const PROP_COLOR: Readonly<Record<number, { core: string; glow: string }>> = {
  [PROP.red]: { core: '#ff4d4d', glow: '#ffd0d0' },
  [PROP.blue]: { core: '#4db2ff', glow: '#d0ecff' },
  [PROP.green]: { core: '#4dff7a', glow: '#d0ffdf' },
  [PROP.purple]: { core: '#b24dff', glow: '#e6d0ff' },
  [PROP.pink]: { core: '#ff4dc4', glow: '#ffd0ef' },
  [PROP.white]: { core: '#ffffff', glow: '#eaf6ff' },
};

interface Colors {
  skin: string;
  skinLight: string;
  skinShade: string;
  skinDark: string;
  blush: string;
  shirt: string;
  shirtShade: string;
  shirtLight: string;
  /** Arm colour: the shirt, or bare skin for a singlet. */
  sleeve: string;
  pants: string;
  pantsShade: string;
  shoesColor: string;
  /** Raw look.shoesColor; 0 keeps each shoe style's original, pre-colour-picker look. */
  shoesColorIndex: number;
  hair: HairOption;
  hairShade: string;
  hairLight: string;
  eyes: number;
  top: number;
  bottom: number;
  shoes: number;
  hat: number;
  glasses: number;
  prop: number;
  /** Loaded design pixels for the print, if any. */
  print: string | undefined;
}

function colorsFor(look: Look): Colors {
  const skin = skinOf(look.body);
  const shirt = topColorOf(look.topColor);
  const pants = bottomColorOf(look.bottomColor);
  const hair = hairOf(look);
  const top = styleIndex(TOP_STYLES, look.top);
  return {
    skin,
    skinLight: shade(skin, 1.08),
    skinShade: shade(skin, 0.86),
    skinDark: shade(skin, 0.62),
    blush: mix(skin, '#e8604f', 0.4),
    shirt,
    shirtShade: shade(shirt, 0.78),
    shirtLight: shade(shirt, 1.18),
    sleeve: top === TOP.singlet ? skin : shirt,
    pants,
    pantsShade: shade(pants, 0.75),
    shoesColor: shoesColorOf(look.shoesColor),
    shoesColorIndex: look.shoesColor,
    hair,
    hairShade: shade(hair.color, 0.72),
    hairLight: shade(hair.color, 1.3),
    eyes: styleIndex(EYE_STYLES, look.eyes),
    top,
    bottom: styleIndex(BOTTOM_STYLES, look.bottom),
    shoes: styleIndex(SHOE_STYLES, look.shoes),
    hat: styleIndex(HAT_STYLES, look.hat),
    glasses: styleIndex(GLASSES_STYLES, look.glasses),
    prop: styleIndex(PROP_STYLES, look.prop),
    print: designPixels(look.print),
  };
}

const isBackView = (view: AvatarView) => view === 'back' || view === 'back3q';

/** Relative luminance, for picking a contrasting accent (e.g. the tie). */
function isDark(color: string): boolean {
  const n = parseInt(color.slice(1), 16);
  return ((n >> 16) & 255) * 0.299 + ((n >> 8) & 255) * 0.587 + (n & 255) * 0.114 < 110;
}

/* -------------------------------------------------------------------------- */
/* Shape helpers                                                              */
/* -------------------------------------------------------------------------- */

/** Light on the left, shadow on the right, across the given span. */
function across(color: string, x0: number, x1: number, light = 1.1, dark = 0.8): Gradient {
  return {
    type: 'linear',
    x0,
    y0: 0,
    x1,
    y1: 0,
    stops: [[0, shade(color, light)], [0.5, color], [1, shade(color, dark)]],
  };
}

/** A soft dark line, for contours inside a sprite. */
const ink = (color: string, factor = 0.55) => shade(color, factor);

/** A limb: a capsule with a gentle side-to-side shading. */
function limb(pc: PixelCanvas, a: Point, b: Point, width: number, color: string, dark = 0.82): void {
  pc.lineF(a.x, a.y, b.x, b.y, between(shade(color, 1.08), shade(color, dark), 0), width);
}

function pt(x: number, y: number): Point {
  return { x, y };
}

/* -------------------------------------------------------------------------- */
/* Legs, bottoms and footwear                                                 */
/* -------------------------------------------------------------------------- */

/** Feet sit on this row; a lifted foot rises above it. */
const FOOT_Y = 60;

interface Stride {
  /** How far each foot is raised, in pixels (front views), or its forward reach (side view). */
  left: number;
  right: number;
}

function strideFor(action: AvatarAction, frame: number): Stride {
  if (action === 'treadWater') {
    const step = frame % 4;
    return { left: step < 2 ? 1 : 0, right: step < 2 ? 0 : 1 };
  }
  const moving = action === 'walk' || action === 'dance' || action === 'swim';
  const step = moving ? frame % 4 : 0;
  return { left: step === 1 ? 2 : 0, right: step === 3 ? 2 : 0 };
}

/** True once the player has picked a shoe colour other than the original default. */
function customShoe(c: Colors): boolean {
  return c.shoesColorIndex !== 0;
}

/** Shoes: a shape at the foot, restyled per footwear. `dir` points toes right (side view) or 0 (front). */
function drawShoe(pc: PixelCanvas, c: Colors, x: number, y: number, side: boolean, lift = 0): void {
  const top = y - 3.6 - lift;
  const w = side ? 7.4 : 5.6;
  const x0 = side ? x - 2.6 : x - w / 2;
  const custom = customShoe(c);
  switch (c.shoes) {
    case SHOES.slippers: {
      pc.rrect(x0 + 0.4, top + 0.5, w - 0.8, 2.4, 1.2, between(c.skinLight, c.skin));
      pc.rrect(x0, top + 2.2, w, 1.4, 0.7, custom ? shade(c.shoesColor, 0.72) : '#2a4a9a');
      pc.rrect(x0 + 0.4, top + 1, w - 0.8, 1.5, 0.7, custom ? between(shade(c.shoesColor, 1.35), c.shoesColor) : between('#5a8ae6', '#3a6fd6'));
      return;
    }
    case SHOES.boots: {
      pc.rrect(x0, top - 0.2, w, 3.8, 1.6, custom ? between(shade(c.shoesColor, 1.16), shade(c.shoesColor, 0.76)) : between('#6b4630', '#4a2f20'));
      pc.rrect(x0 - 0.1, top + 2.6, w + 0.2, 1, 0.5, custom ? shade(c.shoesColor, 0.55) : '#3a2418');
      pc.rrect(x0 + 0.5, top + 0.2, w * 0.45, 0.7, 0.35, custom ? shade(c.shoesColor, 1.45) : '#8a6244');
      return;
    }
    case SHOES.sandals: {
      pc.rrect(x0 + 0.3, top + 0.8, w - 0.6, 2, 1, between(c.skinLight, c.skin));
      pc.rrect(x0, top + 2.4, w, 1.2, 0.6, custom ? c.shoesColor : '#7a4a2e');
      pc.lineF(x0 + w * 0.3, top + 1, x0 + w * 0.3, top + 2.8, custom ? shade(c.shoesColor, 1.28) : '#9a6a44', 0.5);
      pc.lineF(x0 + w * 0.65, top + 1, x0 + w * 0.65, top + 2.8, custom ? shade(c.shoesColor, 1.28) : '#9a6a44', 0.5);
      return;
    }
    case SHOES.loafers: {
      const leather = custom ? c.shoesColor : '#3a2a24';
      pc.rrect(x0, top + 0.4, w, 3, 1.5, between(shade(leather, 1.22), shade(leather, 0.82)));
      pc.rrect(x0 + 0.2, top + 2.6, w - 0.4, 1, 0.5, '#ece6da');
      pc.rrect(x0 + w * 0.32, top + 0.7, w * 0.36, 0.9, 0.4, shade(leather, 1.5));
      return;
    }
    case SHOES.heels: {
      // A pad reaching the same baseline as every other shoe, plus a thin heel post at the back.
      const body = custom ? c.shoesColor : '#b5563f';
      pc.rrect(x0 + 0.6, top + 0.6, w - 1.6, 3, 1.3, between(shade(body, 1.22), shade(body, 0.82)));
      pc.rrect(x0 + w - 1.3, top - 0.4, 0.8, 4, 0.3, shade(body, 0.68));
      return;
    }
    default: {
      const body = custom ? c.shoesColor : SHOE_COLOR;
      pc.rrect(x0, top, w, 3.6, 1.7, between(shade(body, 1.7), body));
      pc.rrect(x0 + 0.2, top + 2.8, w - 0.4, 0.8, 0.4, '#ece6da');
      pc.rrect(x0 + 0.7, top + 0.5, w * 0.35, 0.55, 0.28, custom ? shade(body, 1.6) : '#5a4c46');
    }
  }
}

/** Boots climb the shin; drawn over the leg. */
function drawBootShaft(pc: PixelCanvas, c: Colors, x: number, footTop: number, w = 4.6): void {
  if (c.shoes !== SHOES.boots) return;
  const custom = customShoe(c);
  pc.rrect(x - w / 2, footTop - 3.4, w, 4.4, 1.4, custom ? between(shade(c.shoesColor, 1.16), shade(c.shoesColor, 0.85), 0) : between('#6b4630', '#5a3a2a', 0));
  pc.rrect(x - w / 2, footTop - 3.4, w, 0.9, 0.4, custom ? shade(c.shoesColor, 1.45) : '#7d5638');
}

/** A trouser leg from the hip to the ankle. */
function drawLeg(pc: PixelCanvas, c: Colors, x0: number, y0: number, x1: number, y1: number, width: number, shaded: boolean): void {
  const bare = c.bottom !== BOTTOM.trousers && c.bottom !== BOTTOM.cargo && c.bottom !== BOTTOM.overalls;
  const color = shaded ? c.pantsShade : c.pants;
  if (!bare) {
    limb(pc, pt(x0, y0), pt(x1, y1), width, color, shaded ? 0.9 : 0.8);
    return;
  }
  // Shorts, skirt and sarong show bare legs; shorts cover the top part.
  limb(pc, pt(x0, y0), pt(x1, y1), width * 0.86, shaded ? c.skinShade : c.skin, 0.88);
  if (c.bottom === BOTTOM.shorts) {
    const f = 0.42;
    limb(pc, pt(x0, y0), pt(x0 + (x1 - x0) * f, y0 + (y1 - y0) * f), width, color, shaded ? 0.9 : 0.8);
  }
}

function drawLegs(pc: PixelCanvas, view: AvatarView, action: AvatarAction, frame: number, c: Colors): void {
  if (action === 'sit') {
    drawSittingLegs(pc, view, c);
    return;
  }
  const stride = strideFor(action, frame);
  const moving = action === 'walk' || action === 'dance' || action === 'swim' || action === 'treadWater';
  const step = moving ? frame % 4 : 0;

  if (view === 'side') {
    const striding = step === 1 || step === 3;
    const backX = striding ? 12.6 : 15.2;
    const frontX = striding ? 19.4 : 16.6;
    const backLift = striding ? 0.4 : 0;
    // Far leg first (darker), then the near one.
    drawLeg(pc, c, 15.2, 46, backX, FOOT_Y - 3.4 - backLift, 4.4, true);
    drawShoe(pc, c, backX + 1.4, FOOT_Y - backLift, true);
    drawLeg(pc, c, 16.6, 46, frontX, FOOT_Y - 3.6 - (striding ? 1 : 0), 4.6, false);
    drawBootShaft(pc, c, frontX, FOOT_Y - 3.4 - (striding ? 1 : 0), 4.6);
    drawShoe(pc, c, frontX + 1.4, FOOT_Y - (striding ? 1 : 0), true);
    return;
  }

  const lx = 13.2;
  const rx = 18.8;
  drawLeg(pc, c, lx, 45.5, lx, FOOT_Y - 3.6 - stride.left, 4.7, false);
  drawLeg(pc, c, rx, 45.5, rx, FOOT_Y - 3.6 - stride.right, 4.7, true);
  drawBootShaft(pc, c, lx, FOOT_Y - stride.left - 3.4);
  drawBootShaft(pc, c, rx, FOOT_Y - stride.right - 3.4);
  drawShoe(pc, c, lx, FOOT_Y, false, stride.left);
  drawShoe(pc, c, rx, FOOT_Y, false, stride.right);
}

function drawSittingLegs(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  // The legs hang straight down from the seat and stop short of the floor.
  if (view === 'side') {
    // Thigh forward, shin down, shoe pointing on.
    limb(pc, pt(15, 46), pt(22, 47.4), 4.6, c.pantsShade, 0.9);
    limb(pc, pt(22.4, 47.6), pt(22.6, 55), 4.2, c.pants, 0.85);
    drawShoe(pc, c, 24.4, 58.4, true);
    return;
  }
  if (isBackView(view)) {
    limb(pc, pt(13.2, 46), pt(13.2, 48), 4.6, c.pants);
    limb(pc, pt(18.8, 46), pt(18.8, 48), 4.6, c.pantsShade, 0.9);
    return;
  }
  // Thighs come toward the viewer, so they read as short rounded blocks.
  limb(pc, pt(13.2, 47), pt(13.2, 50.8), 4.8, c.pants);
  limb(pc, pt(18.8, 47), pt(18.8, 50.8), 4.8, c.pantsShade, 0.9);
  limb(pc, pt(13.2, 51), pt(13.2, 55), 4.2, c.pants);
  limb(pc, pt(18.8, 51), pt(18.8, 55), 4.2, c.pantsShade, 0.9);
  drawShoe(pc, c, 13.2, 58.4, false);
  drawShoe(pc, c, 18.8, 58.4, false);
}

/** The waistband, plus the skirt or sarong that replaces the trouser legs. */
function drawBottom(pc: PixelCanvas, view: AvatarView, action: AvatarAction, c: Colors): void {
  const back = isBackView(view);
  const side = view === 'side';
  const sitting = action === 'sit';
  const x0 = side ? 12 : 10;
  const x1 = side ? 21.4 : 22;

  // Hips.
  pc.rrect(x0, 42.6, x1 - x0, 5, 2, across(c.pants, x0, x1, 1.08, 0.82));

  if (c.bottom === BOTTOM.cargo && !sitting) {
    const pocket = shade(c.pants, 0.72);
    if (side) {
      pc.rrect(15.4, 48.6, 3.6, 3.4, 0.8, pocket);
      pc.lineF(15.4, 49.6, 19, 49.6, shade(pocket, 1.3), 0.4);
    } else {
      pc.rrect(11 + (view === 'front3q' ? 0.6 : 0), 48.6, 3, 3.4, 0.8, pocket);
      pc.rrect(18 + (view === 'front3q' ? 0.6 : 0), 48.6, 3, 3.4, 0.8, pocket);
    }
  }

  if (c.bottom === BOTTOM.skirt) {
    const hem = sitting ? 50.6 : 52.6;
    const left = side ? 10.4 : 8.2;
    const right = side ? 23.2 : 24;
    const skirt = new PathBuilder()
      .moveTo(x0, 43.5)
      .lineTo(x1, 43.5)
      .lineTo(right - 0.4, hem - 1.4)
      .quadTo(right + 0.2, hem, right - 1.6, hem)
      .quadTo((left + right) / 2, hem + 1.2, left + 1.6, hem)
      .quadTo(left - 0.2, hem, left + 0.4, hem - 1.4)
      .lineTo(x0, 43.5);
    pc.path(skirt, across(c.pants, left, right, 1.1, 0.8));
    // Pleats.
    for (let i = 1; i < 4; i += 1) {
      const t = i / 4;
      pc.lineF(x0 + (x1 - x0) * t, 45, left + (right - left) * t, hem - 0.4, ink(c.pants, 0.78), 0.4);
    }
    return;
  }

  if (c.bottom === BOTTOM.sarong && !sitting) {
    const light = shade(c.pants, 1.3);
    const wrap = new PathBuilder()
      .moveTo(x0, 43.5)
      .lineTo(x1, 43.5)
      .lineTo(x1 + 0.4, 55)
      .quadTo((x0 + x1) / 2, 57, x0 - 0.4, 55)
      .lineTo(x0, 43.5);
    pc.path(wrap, across(c.pants, x0, x1, 1.08, 0.8));
    if (!back) {
      pc.lineF(x0 + 1, 45, x1 - 3, 54.5, light, 0.7);
      for (let x = x0 + 1; x < x1 - 0.5; x += 2.2) pc.ellipse(x, 55.3, 0.55, 0.55, light);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Torso, tops and prints                                                     */
/* -------------------------------------------------------------------------- */

const SHOULDER_Y = 33.4;

function torsoBox(view: AvatarView): { x0: number; x1: number } {
  return view === 'side' ? { x0: 12, x1: 21 } : { x0: 10, x1: 22 };
}

function drawTorso(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  const { x0, x1 } = torsoBox(view);
  // The neck goes in first so the collar overlaps it.
  pc.rrect(view === 'side' ? 14.2 : 13.8, 26.5, 4.2, 6, 1.6, between(c.skinShade, c.skinDark, 0));
  // A soft wash: lit shoulder, shaded flank.
  pc.rrect(x0, 30.4, x1 - x0, 14.6, 3.6, across(c.shirt, x0, x1, 1.1, 0.78));
  // Shoulder highlight and a fold or two.
  if (view !== 'side') {
    pc.rrect(x0 + 0.8, 31.2, x1 - x0 - 1.6, 1.4, 0.7, `${shade(c.shirt, 1.25)}66`);
  }
  if (view === 'side') {
    pc.lineF(x1 - 1.6, 34, x1 - 1.8, 41, `${shade(c.shirt, 1.2)}66`, 0.6);
  } else if (!isBackView(view)) {
    pc.lineF(x0 + 1.4, 34.5, x0 + 1.4, 40, `${shade(c.shirt, 1.25)}88`, 0.7);
  }
}

/** 3x5 digits for jersey numbers. */
const DIGITS: Record<string, readonly string[]> = {
  '1': ['.#.', '##.', '.#.', '.#.', '###'],
  '0': ['###', '#.#', '#.#', '#.#', '###'],
  '7': ['###', '..#', '.#.', '.#.', '.#.'],
};

function drawDigits(pc: PixelCanvas, text: string, x: number, y: number, color: string): void {
  [...text].forEach((ch, i) => {
    DIGITS[ch]?.forEach((row, ry) => {
      [...row].forEach((bit, rx) => {
        if (bit === '#') pc.rectF(x + i * 4 + rx, y + ry, 1, 1, color);
      });
    });
  });
}

/**
 * A player design, printed across the chest. In profile the chest is half as
 * wide, so every other column is kept; the back of the shirt stays plain.
 * Designs are pixel art by nature, so they stay square-edged.
 */
function drawPrint(pc: PixelCanvas, view: AvatarView, pixels: string): void {
  if (isBackView(view)) return;
  const side = view === 'side';
  const left = side ? 14 : 10;
  const topRow = 32;
  const width = side ? DESIGN_SIZE / 2 : DESIGN_SIZE;
  for (let row = 0; row < DESIGN_SIZE; row += 1) {
    for (let x = 0; x < width; x += 1) {
      const code = pixels[row * DESIGN_SIZE + (side ? x * 2 : x)];
      if (!code || code === '.') continue;
      const color = DESIGN_PALETTE[parseInt(code, 16)];
      if (color) pc.rect(left + x, topRow + row, 1, 1, color);
    }
  }
}

/** Collars, ties, hoods and aprons, drawn over the base torso. */
function styleTop(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  const back = isBackView(view);
  const side = view === 'side';
  const shift = view === 'front3q' ? 1 : 0;
  const white = '#fffaf0';
  const cx = (side ? 16.6 : 16) + shift;

  switch (c.top) {
    case TOP.tee:
      if (back) break;
      // A round neckline.
      pc.ellipse(cx + (side ? 1.4 : 0), 31.1, side ? 1.9 : 2.6, 1.5, c.skinShade);
      pc.path(new PathBuilder().ellipse(cx + (side ? 1.4 : 0), 30.9, side ? 2.4 : 3, 1.9, 0, Math.PI), ink(c.shirt, 0.85));
      break;

    case TOP.singlet:
      if (side) pc.ellipse(cx + 1, 32, 2.6, 2.4, c.skin);
      else if (back) pc.ellipse(cx, 32.2, 4.2, 2.4, c.skinShade);
      else {
        pc.path(new PathBuilder().moveTo(cx - 4.4, 30.2).quadTo(cx - 4.8, 35.8, cx, 35.8).quadTo(cx + 4.8, 35.8, cx + 4.4, 30.2), between(c.skin, c.skinShade));
      }
      return;

    case TOP.polo:
      if (side) {
        pc.poly([pt(17.4, 30.2), pt(21, 30.6), pt(19.2, 33.4)], c.shirtLight);
      } else if (back) {
        pc.rrect(11.4, 30.2, 9.2, 2, 1, c.shirtLight);
      } else {
        pc.poly([pt(12.4 + shift, 30.3), pt(16 + shift, 30.6), pt(15 + shift, 33.8)], c.shirtLight);
        pc.poly([pt(19.6 + shift, 30.3), pt(16 + shift, 30.6), pt(17 + shift, 33.8)], shade(c.shirtLight, 0.94));
        pc.rectF(15.4 + shift, 32, 1.2, 4.6, c.shirtShade);
        pc.ellipse(16 + shift, 33.4, 0.55, 0.55, white);
        pc.ellipse(16 + shift, 35.4, 0.55, 0.55, white);
      }
      return;

    case TOP.office: {
      const tie = isDark(c.shirt) ? '#d9433b' : '#1f3a6b';
      if (side) {
        pc.poly([pt(17.2, 30.2), pt(21, 30.6), pt(19.6, 33.6)], white);
        pc.path(new PathBuilder().moveTo(19.6, 32.2).lineTo(21.2, 32.4).lineTo(21.6, 40).lineTo(20.4, 41.2).lineTo(19.4, 40), tie);
      } else if (back) {
        pc.rrect(11.4, 30.2, 9.2, 2, 1, white);
      } else {
        pc.poly([pt(12.2 + shift, 30.2), pt(16 + shift, 30.8), pt(14.6 + shift, 34)], white);
        pc.poly([pt(19.8 + shift, 30.2), pt(16 + shift, 30.8), pt(17.4 + shift, 34)], shade(white, 0.92));
        pc.path(
          new PathBuilder()
            .moveTo(15.2 + shift, 31.6)
            .lineTo(16.8 + shift, 31.6)
            .lineTo(17.4 + shift, 39.6)
            .lineTo(16 + shift, 41.2)
            .lineTo(14.6 + shift, 39.6),
          between(shade(tie, 1.2), shade(tie, 0.8)),
        );
        pc.rrect(15 + shift, 30.8, 2, 1.8, 0.7, shade(tie, 0.8));
      }
      return;
    }

    case TOP.hoodie:
      if (side) {
        pc.rrect(11.4, 27.8, 6, 5.4, 2.6, c.shirtShade);
        pc.lineF(19.6, 33.4, 19.6, 36, white, 0.5);
      } else if (back) {
        pc.rrect(10.4, 26.8, 11.2, 6.4, 3, between(c.shirtShade, shade(c.shirt, 0.62)));
      } else {
        pc.rrect(10.6, 29.2, 10.8, 3.2, 1.6, c.shirtShade);
        pc.lineF(14.4 + shift, 32.6, 14.4 + shift, 35.6, white, 0.55);
        pc.lineF(17.6 + shift, 32.6, 17.6 + shift, 35.6, white, 0.55);
        pc.rrect(12.2 + shift, 38.6, 7.6, 3.8, 1.4, between(shade(c.shirt, 0.86), c.shirtShade));
      }
      return;

    case TOP.kebaya: {
      const lace = '#fffaf0';
      if (side) {
        for (let x = 12.4; x < 21; x += 1.6) pc.ellipse(x, 43.4, 0.85, 0.85, lace);
        pc.ellipse(19.4, 35.4, 0.7, 0.7, '#f19bb7');
      } else if (back) {
        for (let x = 10.6; x < 22; x += 1.6) pc.ellipse(x, 43.4, 0.85, 0.85, lace);
      } else {
        pc.path(new PathBuilder().moveTo(12.4 + shift, 30.2).quadTo(13.6 + shift, 34.8, 16 + shift, 33.6).quadTo(18.4 + shift, 34.8, 19.6 + shift, 30.2), between(c.skin, c.skinShade));
        pc.ellipse(15.4 + shift, 35.4, 0.8, 0.8, '#f0bf36');
        pc.ellipse(16.8 + shift, 35.4, 0.8, 0.8, '#f0bf36');
        for (const [x, y] of [[11.5, 37.5], [20.5, 38.4], [13.5, 40.4], [18.5, 35.4]] as const) {
          pc.ellipse(x + shift, y, 0.6, 0.6, '#f19bb7');
        }
        for (let x = 10.6; x < 22; x += 1.6) pc.ellipse(x, 43.4, 0.85, 0.85, lace);
      }
      return;
    }

    case TOP.jersey: {
      const number = isDark(c.shirt) ? white : '#1d1d1d';
      if (side) {
        pc.rrect(12, 36, 9, 1.2, 0.5, white);
      } else if (back) {
        drawDigits(pc, '10', 12, 34, number);
      } else {
        pc.path(new PathBuilder().ellipse(cx, 30.8, 3.6, 2, 0, Math.PI), white);
        drawDigits(pc, '7', 15 + shift, 35, number);
      }
      return;
    }

    case TOP.batik: {
      const motif = shade(mix(c.shirt, '#f0bf36', 0.45), 1.05);
      const dot = (px: number, py: number) => pc.ellipse(px, py, 0.85, 0.85, motif);
      if (side) {
        pc.poly([pt(17.2, 30.2), pt(21, 30.6), pt(19.2, 33.4)], c.shirtLight);
        for (let y = 31.4; y < 43; y += 2.6) dot(16.4, y);
      } else if (back) {
        for (let x = 11.2; x < 21.6; x += 2.6) for (let y = 31.4; y < 43; y += 2.8) dot(x, y + ((x / 2.6) % 2) * 1.3);
      } else {
        pc.poly([pt(12.4 + shift, 30.3), pt(16 + shift, 30.6), pt(15 + shift, 33.8)], c.shirtLight);
        pc.poly([pt(19.6 + shift, 30.3), pt(16 + shift, 30.6), pt(17 + shift, 33.8)], shade(c.shirtLight, 0.94));
        for (let x = 11.2; x < 21.6; x += 2.6) for (let y = 32.4; y < 43; y += 2.8) dot(x + shift, y + ((x / 2.6) % 2) * 1.3);
      }
      return;
    }

    case TOP.varsity: {
      const trim = isDark(c.shirt) ? white : '#1d1d1d';
      if (side) {
        pc.rrect(19.2, 30.2, 1.8, 11.6, 0.8, trim);
        pc.poly([pt(17.2, 30.2), pt(21, 30.6), pt(19.2, 33.4)], trim);
      } else if (back) {
        pc.rrect(10.2, 30.2, 11.6, 1.8, 0.8, trim);
        drawDigits(pc, '1', 13.6, 34.6, isDark(c.shirt) ? '#1d1d1d' : white);
      } else {
        pc.rrect(10.2 + shift, 30.2, 1.8, 12.4, 0.8, trim);
        pc.rrect(20 + shift, 30.2, 1.8, 12.4, 0.8, trim);
        pc.poly([pt(12.4 + shift, 30.3), pt(16 + shift, 30.7), pt(14.8 + shift, 33.8)], trim);
        pc.poly([pt(19.6 + shift, 30.3), pt(16 + shift, 30.7), pt(17.2 + shift, 33.8)], shade(trim, 0.88));
        pc.rrect(13.6 + shift, 38, 4.8, 3.4, 1, trim);
      }
      return;
    }

    case TOP.turtleneck: {
      const band = shade(c.shirt, 0.86);
      const bandLight = shade(c.shirt, 1.12);
      if (side) {
        pc.rrect(13.4, 26.4, 5.6, 4.6, 2.2, band);
        pc.rrect(13.4, 26.4, 5.6, 1, 0.5, bandLight);
      } else {
        const bx = view === 'front3q' ? 12.6 : 12.2;
        pc.rrect(bx, 26.4, 7.6, 4.6, 2.2, band);
        pc.rrect(bx, 26.4, 7.6, 1, 0.5, bandLight);
      }
      return;
    }

    case TOP.apron: {
      const apron = '#f4efe4';
      if (side) {
        pc.rrect(19, 33.6, 3, 11.4, 1.2, between(apron, '#dcd5c6', 0));
        pc.lineF(17.4, 31.2, 19.4, 34, apron, 0.7);
      } else if (back) {
        pc.lineF(10.4, 37.4, 21.6, 37.4, apron, 0.9);
        pc.rrect(14.6, 37, 2.8, 3.4, 1.2, apron);
      } else {
        pc.rrect(11 + shift, 33.6, 10, 11.6, 2.2, between(apron, '#dcd5c6', 0));
        pc.rrect(13.2 + shift, 39.4, 5.6, 3.4, 1, '#e6dfd0');
        pc.lineF(12.6 + shift, 31, 13.4 + shift, 34, apron, 0.8);
        pc.lineF(19.4 + shift, 31, 18.6 + shift, 34, apron, 0.8);
      }
      return;
    }

    default:
      return;
  }
}

/* -------------------------------------------------------------------------- */
/* Arms                                                                       */
/* -------------------------------------------------------------------------- */

type ArmState = 'down' | 'up' | 'wave-a' | 'wave-b' | 'hold' | 'sip' | 'swing-f' | 'swing-b';

interface ArmShape {
  shoulder: Point;
  elbow: Point;
  hand: Point;
}

/**
 * Where an arm's joints go. `side` -1 is the viewer's left arm, +1 the right;
 * in profile only one arm shows and `side` is +1 (the near arm).
 */
function armShape(view: AvatarView, side: -1 | 1, state: ArmState, narrow: boolean): ArmShape {
  if (view === 'side') {
    const s = pt(16.2, SHOULDER_Y);
    const at = (ex: number, ey: number, hx: number, hy: number): ArmShape => ({
      shoulder: s,
      elbow: pt(s.x + ex, s.y + ey),
      hand: pt(s.x + hx, s.y + hy),
    });
    switch (state) {
      case 'up':
      case 'wave-a':
        return at(2.4, -3, 3, -10.4);
      case 'wave-b':
        return at(2.4, -3, 4.2, -9.8);
      case 'hold':
        return at(1, 5.6, 5, 5.4);
      case 'sip':
        return at(2, 4.6, 5.6, -6.6);
      case 'swing-f':
        return at(1.8, 5, 3.4, 9.6);
      case 'swing-b':
        return at(-1.8, 5, -3.4, 9.6);
      default:
        return at(-0.2, 5.4, 0.2, 10.4);
    }
  }
  const sx = side < 0 ? 9.4 : narrow ? 21.4 : 22.6;
  const s = pt(sx, SHOULDER_Y);
  const out = side;
  const at = (ex: number, ey: number, hx: number, hy: number): ArmShape => ({
    shoulder: s,
    elbow: pt(s.x + ex * out, s.y + ey),
    hand: pt(s.x + hx * out, s.y + hy),
  });
  switch (state) {
    case 'up':
      return at(2.6, -2.6, 3.4, -9.6);
    case 'wave-a':
      return at(3, -2.4, 3.6, -9.4);
    case 'wave-b':
      return at(3, -2.4, 5, -8.6);
    case 'hold':
      return at(0.9, 5.4, -1.6, 6.6);
    case 'sip':
      return at(1.2, 3.6, -3.4, -5.6);
    case 'swing-f':
      return at(0.6, 5, 0.8, 8.8);
    case 'swing-b':
      return at(1, 5.4, 1.4, 10.6);
    default:
      return at(0.8, 5.4, 1.2, 10.4);
  }
}

/** Draws one arm and returns where the hand ended up. */
function drawArm(pc: PixelCanvas, shape: ArmShape, sleeve: string, hand: string, shaded: boolean, width = 3.5): Point {
  const color = shaded ? shade(sleeve, 0.82) : sleeve;
  const skin = shaded ? shade(hand, 0.9) : hand;
  // A soft contour so the arm reads against the torso it hangs over.
  const edge = `${ink(color, 0.5)}b0`;
  pc.lineF(shape.shoulder.x, shape.shoulder.y, shape.elbow.x, shape.elbow.y, edge, width + 0.8);
  pc.lineF(shape.elbow.x, shape.elbow.y, shape.hand.x, shape.hand.y, edge, width * 0.92 + 0.8);
  limb(pc, shape.shoulder, shape.elbow, width, color, 0.84);
  limb(pc, shape.elbow, shape.hand, width * 0.92, color, 0.84);
  pc.ellipse(shape.hand.x, shape.hand.y + 0.5, 1.75, 1.85, between(shade(skin, 1.06), shade(skin, 0.9), 45));
  return shape.hand;
}

/* -------------------------------------------------------------------------- */
/* Head, eyes and hair                                                        */
/* -------------------------------------------------------------------------- */

/**
 * One eye centred on (x, y). `outer` points away from the nose (-1 for the
 * viewer's left eye), for lashes.
 */
function drawEye(pc: PixelCanvas, x: number, y: number, outer: -1 | 1, c: Colors, blinking: boolean, scale = 1): void {
  if (blinking) {
    pc.lineF(x - 1.3 * scale, y + 0.4, x + 1.3 * scale, y + 0.4, EYE_COLOR, 0.7);
    return;
  }
  switch (c.eyes) {
    case 1: // happy arcs
      pc.path(new PathBuilder().ellipse(x, y + 1.1, 1.55 * scale, 1.4, Math.PI, Math.PI * 2), EYE_COLOR);
      pc.path(new PathBuilder().ellipse(x, y + 1.35, 1.0 * scale, 0.8, Math.PI, Math.PI * 2), c.skin);
      return;
    case 2: // sleepy, heavy lids
      pc.ellipse(x, y + 0.5, 0.95 * scale, 0.85, EYE_COLOR);
      pc.path(new PathBuilder().ellipse(x, y + 0.2, 1.7 * scale, 1.3, Math.PI, Math.PI * 2), c.skinDark);
      pc.rectF(x - 1.7 * scale, y + 0.05, 3.4 * scale, 0.5, c.skinDark);
      return;
    case 3: // lashes
      pc.ellipse(x, y, 1.0 * scale, 1.6, EYE_COLOR);
      pc.ellipse(x - 0.35, y - 0.7, 0.4, 0.45, '#ffffff');
      pc.lineF(x + outer * 0.9 * scale, y - 0.9, x + outer * 2.3 * scale, y - 1.7, EYE_COLOR, 0.6);
      pc.lineF(x + outer * 1 * scale, y - 0.1, x + outer * 2.4 * scale, y - 0.5, EYE_COLOR, 0.55);
      return;
    case 4: // determined, with brows
      pc.ellipse(x, y + 0.2, 0.95 * scale, 1.35, EYE_COLOR);
      pc.lineF(x - outer * 1.6 * scale, y - 2.5 - outer * 0.3, x + outer * 1.6 * scale, y - 3.2 + outer * 0.3 + 0.5, c.hairShade, 0.8);
      return;
    case 5: // big sparkly
      pc.ellipse(x, y, 1.25 * scale, 2.0, EYE_COLOR);
      pc.ellipse(x + 0.1, y + 0.7, 0.8 * scale, 1.0, '#3a6fd6');
      pc.ellipse(x - 0.4, y - 0.9, 0.55, 0.6, '#ffffff');
      pc.ellipse(x + 0.5, y + 1.1, 0.3, 0.3, '#ffffff');
      return;
    case 6: // wink: the outer eye closes, the inner one stays open
      if (outer === 1) {
        pc.lineF(x - 1.3 * scale, y + 0.35, x + 1.3 * scale, y - 0.25, EYE_COLOR, 0.75);
        pc.lineF(x + outer * 1.1 * scale, y - 0.7, x + outer * 1.9 * scale, y - 1.3, EYE_COLOR, 0.5);
      } else {
        pc.ellipse(x, y, 1.0 * scale, 1.55, EYE_COLOR);
        pc.ellipse(x - 0.35, y - 0.65, 0.38, 0.42, '#ffffff');
      }
      return;
    case 7: // starlight: a deep violet iris with a starry double glint
      pc.ellipse(x, y, 1.3 * scale, 2.1, EYE_COLOR);
      pc.ellipse(x, y + 0.75, 0.85 * scale, 1.05, '#8a52c8');
      pc.ellipse(x - 0.4, y - 1, 0.6, 0.65, '#ffffff');
      pc.ellipse(x + 0.55, y + 1.15, 0.3, 0.3, '#ffffff');
      pc.lineF(x - 0.85 * scale, y - 0.15, x - 0.05 * scale, y - 0.15, '#ffffffcc', 0.35);
      pc.lineF(x - 0.45 * scale, y - 0.55, x - 0.45 * scale, y + 0.25, '#ffffffcc', 0.35);
      return;
    default:
      pc.ellipse(x, y, 0.95 * scale, 1.55, EYE_COLOR);
      pc.ellipse(x - 0.35, y - 0.65, 0.38, 0.42, '#ffffff');
  }
}

function drawHead(pc: PixelCanvas, view: AvatarView, c: Colors, blinking: boolean): void {
  const skinWash = (x0: number, x1: number): Gradient => ({
    type: 'linear',
    x0,
    y0: 0,
    x1,
    y1: 0,
    stops: [[0, c.skinLight], [0.55, c.skin], [1, c.skinShade]],
  });

  if (view === 'side') {
    pc.rrect(9, 9.2, 15, 19.4, 7.4, skinWash(9, 24));
    // Nose, ear, cheek, mouth and eye.
    pc.ellipse(24.1, 21.6, 1.15, 1.45, between(c.skinLight, c.skin, 0));
    pc.ellipse(14.6, 21, 1.5, 2.3, c.skinShade);
    pc.ellipse(14.7, 21.1, 0.7, 1.4, c.skinDark);
    pc.ellipse(20.4, 24.2, 1.7, 1, `${c.blush}aa`);
    pc.lineF(21.4, 26, 23, 25.8, c.skinDark, 0.6);
    drawEye(pc, 20.6, 20.8, 1, c, blinking);
    return;
  }

  pc.rrect(8, 9.2, 16, 19.4, 7.6, skinWash(8, 24));
  if (isBackView(view)) return;

  // Ears; the 3/4 view hides the far one.
  pc.ellipse(7.6, 21.2, 1.3, 2.2, c.skin);
  if (view === 'front') pc.ellipse(24.4, 21.2, 1.3, 2.2, c.skinShade);

  const shift = view === 'front3q' ? 2 : 0;
  const leftEye = 13 + shift;
  const rightEye = 19 + shift;
  drawEye(pc, leftEye, 20.9, -1, c, blinking);
  drawEye(pc, rightEye, 20.9, 1, c, blinking);
  pc.ellipse(leftEye - 2.2, 23.7, 1.5, 0.9, `${c.blush}aa`);
  pc.ellipse(rightEye + 2.2, 23.7, 1.5, 0.9, `${c.blush}aa`);
  // A tiny nose and a small smile.
  pc.ellipse(16 + shift, 23.6, 0.8, 0.5, c.skinShade);
  pc.path(new PathBuilder().ellipse(16 + shift, 24.9, 1.5, 1.1, 0.15, Math.PI - 0.15), c.skinDark);
  pc.path(new PathBuilder().ellipse(16 + shift, 24.85, 1.1, 0.7, 0.15, Math.PI - 0.15), c.skin);
}

/** A hair mass over the crown. Shared by most front and back styles. */
function crown(back: boolean): PathBuilder {
  const p = new PathBuilder();
  if (back) {
    return p
      .moveTo(7, 22)
      .curveTo(6, 12, 9.4, 6.6, 16, 6.6)
      .curveTo(22.6, 6.6, 26, 12, 25, 22)
      .quadTo(24.6, 26, 22.4, 26.4)
      .lineTo(9.6, 26.4)
      .quadTo(7.4, 26, 7, 22);
  }
  return p
    .moveTo(7.2, 19)
    .curveTo(6.2, 12.4, 9.6, 6.7, 16, 6.7)
    .curveTo(22.4, 6.7, 25.8, 12.4, 24.8, 19)
    .lineTo(23.4, 19)
    .curveTo(23.4, 16, 22.4, 14.4, 20.8, 13.6)
    .quadTo(18.6, 15.6, 16.6, 13.6)
    .quadTo(14.4, 16.4, 12, 13.6)
    .quadTo(10, 14.4, 9.2, 16.2)
    .quadTo(8.6, 17.4, 8.6, 19)
    .lineTo(7.2, 19);
}

function hairPaint(c: Colors, x0 = 7, x1 = 25): Gradient {
  return {
    type: 'linear',
    x0,
    y0: 0,
    x1,
    y1: 0,
    stops: [[0, shade(c.hair.color, 1.12)], [0.55, c.hair.color], [1, c.hairShade]],
  };
}

function hairShine(pc: PixelCanvas, c: Colors, x0: number, x1: number, y: number): void {
  pc.path(new PathBuilder().ellipse((x0 + x1) / 2, y + 1.4, (x1 - x0) / 2, 1.4, Math.PI * 1.08, Math.PI * 1.92), `${c.hairLight}bb`);
}

function drawHair(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  const { style } = c.hair;
  if (view === 'side') {
    drawSideHair(pc, c);
    return;
  }
  const back = isBackView(view);
  const paint = hairPaint(c);

  if (style === 'cap') {
    drawCapHair(pc, view, c);
    return;
  }

  // Long hair falls behind the shoulders, so it is drawn first.
  if (style === 'long') {
    if (back) pc.rrect(6.2, 10, 19.6, 21, 7, between(c.hair.color, c.hairShade, 0));
    else {
      pc.path(new PathBuilder().moveTo(9.4, 12).curveTo(5.4, 13.4, 4.8, 26, 6.4, 29.6).quadTo(8.2, 31, 9.8, 28.8).lineTo(9.8, 16), shade(c.hair.color, 1.04));
      pc.path(new PathBuilder().moveTo(22.6, 12).curveTo(26.6, 13.4, 27.2, 26, 25.6, 29.6).quadTo(23.8, 31, 22.2, 28.8).lineTo(22.2, 16), c.hairShade);
    }
  } else if (style === 'bob') {
    if (back) pc.rrect(6.4, 10, 19.2, 15.6, 6.4, between(c.hair.color, c.hairShade, 0));
    else {
      pc.path(new PathBuilder().moveTo(9.4, 12).curveTo(5.6, 13.4, 5.4, 22, 6.6, 23.6).quadTo(8.4, 25, 9.8, 23).lineTo(9.8, 16), shade(c.hair.color, 1.04));
      pc.path(new PathBuilder().moveTo(22.6, 12).curveTo(26.4, 13.4, 26.6, 22, 25.4, 23.6).quadTo(23.6, 25, 22.2, 23).lineTo(22.2, 16), c.hairShade);
    }
  }

  if (back) {
    // From behind the hair covers the whole head.
    if (style === 'buzz') {
      pc.path(crown(true), paint);
      pc.rrect(8, 21, 16, 5.6, 2, c.skinShade);
      pc.path(new PathBuilder().moveTo(8, 21).lineTo(24, 21).lineTo(24, 16).lineTo(8, 16), `${c.hairShade}00`);
    } else {
      pc.path(crown(true), paint);
    }
    if (view === 'back3q') pc.ellipse(23.6, 21, 1.4, 3.6, c.skinShade);
    hairShine(pc, c, 9.4, 18, 8.2);
  } else if (style === 'buzz') {
    // A close-cropped hairline: a thin cap, skin showing through.
    pc.path(
      new PathBuilder()
        .moveTo(7.6, 17.4)
        .curveTo(6.6, 12, 9.6, 7.4, 16, 7.4)
        .curveTo(22.4, 7.4, 25.4, 12, 24.4, 17.4)
        .lineTo(23.4, 17)
        .curveTo(22.4, 12.6, 20, 11.4, 16, 11.4)
        .curveTo(12, 11.4, 9.6, 12.6, 8.6, 17),
      paint,
    );
  } else {
    pc.path(crown(false), paint);
    hairShine(pc, c, 9.4, 18.6, 7.4);
  }

  switch (style) {
    case 'spiky':
      for (const [x, h, s] of [[9.4, 4.4, 0], [13.2, 3.4, 1], [17.4, 3.4, 2], [21.4, 4.6, 3]] as const) {
        pc.poly([pt(x - 2.1, 9.4), pt(x + 0.2, 6.6 - h + 2.6), pt(x + 2.6, 9.4)], s < 2 ? shade(c.hair.color, 1.02) : c.hairShade);
      }
      break;
    case 'bun':
      pc.ellipse(16, 5.8, 3.9, 3.3, between(shade(c.hair.color, 1.15), c.hairShade, 60));
      pc.ellipse(15, 4.9, 1.6, 0.9, `${c.hairLight}99`);
      pc.rrect(12.6, 8.4, 6.8, 1.3, 0.6, shade(c.hair.color, 0.6));
      break;
    case 'side':
      if (!back) {
        // A swept fringe across the forehead.
        pc.path(
          new PathBuilder()
            .moveTo(8.8, 12.6)
            .curveTo(13, 12, 19, 12.2, 23.8, 16.2)
            .quadTo(22, 15.4, 19.4, 15.6)
            .quadTo(15, 14.4, 9.4, 16.2)
            .lineTo(8.8, 12.6),
          hairPaint(c),
        );
        pc.lineF(15, 12.9, 22, 15.2, `${c.hairLight}88`, 0.55);
      }
      break;
    case 'curly': {
      const balls: Array<[number, number, number]> = [[8.2, 11, 3], [11, 7.6, 3.2], [16, 6.4, 3.4], [21, 7.6, 3.2], [23.8, 11, 3], [7.4, 16.2, 2.4], [24.6, 16.2, 2.4]];
      for (const [x, y, r] of balls) pc.ellipse(x, y, r, r, between(shade(c.hair.color, 1.15), x > 16 ? c.hairShade : c.hair.color, 55));
      break;
    }
    case 'afro': {
      const balls: Array<[number, number, number]> = [
        [16, 5, 4.6],
        [9, 6.4, 3.8],
        [23, 6.4, 3.8],
        [12.2, 4, 3.4],
        [19.8, 4, 3.4],
        [5.6, 11.6, 3.6],
        [26.4, 11.6, 3.6],
        [7, 17.4, 3],
        [25, 17.4, 3],
      ];
      for (const [x, y, r] of balls) pc.ellipse(x, y, r, r, between(shade(c.hair.color, 1.18), x > 16 ? c.hairShade : shade(c.hair.color, 0.94), 55));
      hairShine(pc, c, 9, 20, 5);
      break;
    }
    case 'ponytail':
      if (back) {
        pc.path(
          new PathBuilder()
            .moveTo(13.6, 11)
            .curveTo(12.8, 20, 14, 30, 15, 38)
            .quadTo(16, 39.6, 17, 38)
            .curveTo(18, 30, 19.2, 20, 18.4, 11)
            .quadTo(16, 9.2, 13.6, 11),
          hairPaint(c, 12.6, 19.4),
        );
        pc.rrect(13.4, 10.2, 5.2, 2, 1, shade(c.hair.color, 0.6));
      } else {
        pc.ellipse(16, 7.6, 2.2, 1.1, shade(c.hair.color, 0.6));
      }
      break;
    case 'braids':
      for (const side of [-1, 1] as const) {
        const x = 16 + side * 9.2;
        pc.lineF(x, 13, x + side * 0.6, 33, hairPaint(c, x - 2, x + 2), 2.6);
        pc.ellipse(x + side * 0.7, 34, 1.3, 1.3, c.hairShade);
        for (let ty = 16; ty < 32; ty += 4) pc.lineF(x - 1.3, ty, x + 1.3, ty, shade(c.hair.color, 0.55), 0.4);
      }
      break;
    default:
      break;
  }
}

function drawCapHair(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  const cap = c.hair.accent ?? '#c8423b';
  const back = isBackView(view);
  // Hair peeks out beneath the cap.
  pc.path(crown(back), hairPaint(c));
  const dome = new PathBuilder()
    .moveTo(7.2, 13.4)
    .curveTo(6.6, 6.6, 10.6, 5.2, 16, 5.2)
    .curveTo(21.4, 5.2, 25.4, 6.6, 24.8, 13.4)
    .lineTo(7.2, 13.4);
  pc.path(dome, across(cap, 7, 25, 1.14, 0.78));
  pc.ellipse(15.4, 9, 2.1, 1.7, '#f4efe4');
  pc.path(new PathBuilder().ellipse(13, 7.6, 4.6, 1.6, Math.PI * 1.05, Math.PI * 1.9), `${shade(cap, 1.35)}99`);
  if (!back) pc.path(new PathBuilder().moveTo(6.8, 13).quadTo(16, 16.4, 25.6, 13).quadTo(16, 14.4, 6.8, 13), shade(cap, 0.66));
}

function drawSideHair(pc: PixelCanvas, c: Colors): void {
  const { style } = c.hair;
  const paint = hairPaint(c, 8, 24);
  const base = new PathBuilder()
    .moveTo(23.6, 14.6)
    .curveTo(23.8, 9.6, 21.2, 6.7, 16, 6.7)
    .curveTo(10, 6.7, 6.6, 11, 7.4, 18)
    .curveTo(7.8, 21.6, 9.4, 24.2, 11.8, 25.2)
    .quadTo(13.2, 24.4, 13.2, 22)
    .lineTo(13.4, 16.6)
    .quadTo(14.6, 13.8, 16.2, 12.8)
    .quadTo(18, 14.6, 20.4, 13.4)
    .quadTo(22.4, 12.6, 23.6, 14.6);

  if (style === 'cap') {
    const cap = c.hair.accent ?? '#c8423b';
    pc.path(base, paint);
    pc.path(
      new PathBuilder()
        .moveTo(8.2, 13)
        .curveTo(7.4, 6.4, 11, 5.2, 16, 5.2)
        .curveTo(21.4, 5.2, 24.4, 8, 23.6, 13)
        .lineTo(8.2, 13),
      across(cap, 8, 24, 1.14, 0.78),
    );
    pc.path(new PathBuilder().moveTo(20, 12.6).quadTo(27.6, 12.4, 28.4, 15.2).quadTo(22.6, 15, 19.4, 14.2), shade(cap, 0.7));
    pc.ellipse(15.2, 8.4, 1.8, 1.5, '#f4efe4');
    return;
  }

  if (style === 'long') {
    pc.path(
      new PathBuilder().moveTo(9, 11).curveTo(5.4, 13, 5.6, 29.4, 8.4, 30.6).quadTo(11.2, 32, 13.6, 30.4).quadTo(14, 24, 14, 18),
      between(shade(c.hair.color, 1.04), c.hairShade, 0),
    );
  } else if (style === 'bob') {
    pc.path(
      new PathBuilder().moveTo(9, 11).curveTo(5.6, 13, 5.8, 24.4, 8.4, 25.2).quadTo(11.4, 26.2, 13.6, 24.6).quadTo(14, 20, 14, 16),
      between(shade(c.hair.color, 1.04), c.hairShade, 0),
    );
  }
  pc.path(base, paint);
  hairShine(pc, c, 11, 19, 7.4);

  switch (style) {
    case 'spiky':
      for (const [x, h] of [[11, 3.4], [15, 4.4], [19, 3.4]] as const) {
        pc.poly([pt(x - 2.1, 9), pt(x + 0.1, 8.4 - h), pt(x + 2.4, 9)], x > 15 ? c.hairShade : c.hair.color);
      }
      break;
    case 'bun':
      pc.ellipse(9.4, 8.6, 3.7, 3.7, between(shade(c.hair.color, 1.12), c.hairShade, 60));
      break;
    case 'curly':
      for (const [x, y, r] of [[10, 8.6, 3], [15, 6.8, 3], [20, 8.4, 2.8], [9, 16, 3], [13, 12, 2.6]] as const) {
        pc.ellipse(x, y, r, r, between(shade(c.hair.color, 1.14), c.hairShade, 55));
      }
      break;
    case 'buzz':
      pc.path(new PathBuilder().moveTo(9, 24).curveTo(8, 18, 9, 14, 12, 12.6).lineTo(15, 15).lineTo(15, 24), c.skinShade);
      break;
    case 'afro':
      for (const [x, y, r] of [[9, 7.6, 3.6], [15, 5.4, 3.8], [21, 7, 3.6], [24, 12, 3.2], [8, 13.4, 3], [7, 19.4, 2.8], [12, 4.2, 3]] as const) {
        pc.ellipse(x, y, r, r, between(shade(c.hair.color, 1.16), c.hairShade, 55));
      }
      break;
    case 'ponytail':
      pc.path(
        new PathBuilder()
          .moveTo(7.4, 12)
          .curveTo(4.6, 18, 5.2, 27, 7.6, 33)
          .quadTo(9, 34.4, 10, 33)
          .curveTo(9, 26, 9.6, 18, 9.4, 12)
          .quadTo(8.4, 10.6, 7.4, 12),
        hairPaint(c, 4, 10),
      );
      pc.rrect(6.6, 11, 3.6, 1.8, 0.9, shade(c.hair.color, 0.6));
      break;
    case 'braids':
      pc.lineF(14.6, 15, 15.4, 32, hairPaint(c, 12, 18), 2.6);
      pc.ellipse(15.2, 33, 1.3, 1.3, c.hairShade);
      for (let ty = 18; ty < 31; ty += 4) pc.lineF(13.6, ty, 16.6, ty, shade(c.hair.color, 0.55), 0.4);
      break;
    default:
      break;
  }
}

/* -------------------------------------------------------------------------- */
/* Accessories                                                                */
/* -------------------------------------------------------------------------- */

function drawGlasses(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  if (c.glasses === GLASSES.none || isBackView(view)) return;

  const frame =
    c.glasses === GLASSES.hearts
      ? '#ff5c9a'
      : c.glasses === GLASSES.round
        ? '#3a2a24'
        : c.glasses === GLASSES.monocle
          ? GOLD_HAT
          : c.glasses === GLASSES.diamond
            ? '#8fd9ff'
            : '#1d1d1d';
  const lens = (x: number, y: number, r = 2.2) => {
    switch (c.glasses) {
      case GLASSES.round:
        pc.ellipse(x, y, r + 0.4, r + 0.4, frame);
        pc.ellipse(x, y, r - 0.3, r - 0.3, '#dff3ff66');
        pc.ellipse(x - 0.8, y - 0.8, 0.6, 0.4, '#ffffffcc');
        return;
      case GLASSES.shades:
        pc.rrect(x - r - 0.4, y - r + 0.6, r * 2 + 0.8, r * 1.7, 1, between('#3a3f4a', '#12141a'));
        pc.rrect(x - r + 0.2, y - r + 1, r * 1.1, 0.7, 0.3, '#6a7a8a');
        return;
      case GLASSES.nerd:
        pc.rrect(x - r - 0.4, y - r - 0.4, r * 2 + 0.8, r * 2 + 0.6, 1.1, frame);
        pc.rrect(x - r + 0.3, y - r + 0.3, r * 2 - 0.6, r * 2 - 0.8, 0.7, '#e8f5ff88');
        pc.ellipse(x - 0.9, y - 0.9, 0.55, 0.35, '#ffffffcc');
        return;
      case GLASSES.hearts: {
        const heart = new PathBuilder()
          .moveTo(x, y + r * 0.9)
          .curveTo(x - r * 1.5, y - r * 0.1, x - r * 0.9, y - r * 1.3, x, y - r * 0.4)
          .curveTo(x + r * 0.9, y - r * 1.3, x + r * 1.5, y - r * 0.1, x, y + r * 0.9);
        pc.path(heart, frame);
        pc.ellipse(x - r * 0.4, y - r * 0.3, 0.55, 0.4, '#ffc2d8');
        return;
      }
      case GLASSES.catEye: {
        const wing = new PathBuilder()
          .moveTo(x - r - 0.6, y + 0.4)
          .quadTo(x - r - 0.4, y - r - 0.4, x, y - r * 0.55)
          .quadTo(x + r + 0.4, y - r - 0.4, x + r + 0.6, y + 0.4)
          .quadTo(x, y + r * 0.75, x - r - 0.6, y + 0.4);
        pc.path(wing, frame);
        pc.ellipse(x, y + 0.15, r - 0.5, r - 0.6, '#dff3ff66');
        pc.ellipse(x - 0.8, y - 0.5, 0.55, 0.35, '#ffffffcc');
        return;
      }
      case GLASSES.monocle:
        pc.ellipse(x, y, r + 0.1, r + 0.1, frame);
        pc.ellipse(x, y, r - 0.6, r - 0.6, '#dff3ff77');
        pc.ellipse(x - 0.7, y - 0.7, 0.5, 0.35, '#ffffffcc');
        return;
      case GLASSES.diamond:
        pc.rrect(x - r - 0.4, y - r + 0.6, r * 2 + 0.8, r * 1.7, 0.6, between('#eaffff', '#bfe9ff'));
        pc.lineF(x - r + 0.2, y - r * 0.3 + 0.6, x, y - r + 0.7, '#ffffffcc', 0.4);
        pc.lineF(x, y - r + 0.7, x + r - 0.2, y - r * 0.3 + 0.6, '#ffffffcc', 0.4);
        pc.rrect(x - r + 0.3, y - r + 1, r * 1.1, 0.6, 0.3, '#ffffffaa');
        return;
      default:
    }
  };

  if (c.glasses === GLASSES.monocle) {
    // Worn on one eye only, with a fine chain instead of the usual arms.
    if (view === 'side') {
      lens(20.6, 20.9);
      pc.lineF(20.9, 23, 22.4, 27.4, frame, 0.5);
      return;
    }
    const shift = view === 'front3q' ? 2 : 0;
    const right = 19 + shift;
    lens(right, 20.9);
    pc.lineF(right + 1.6, 23, right + 3, 28.4, frame, 0.5);
    return;
  }

  if (view === 'side') {
    lens(20.6, 20.9);
    pc.lineF(14.6, 20, 18.5, 20.2, frame, 0.6);
    return;
  }
  const shift = view === 'front3q' ? 2 : 0;
  const left = 13 + shift;
  const right = 19 + shift;
  lens(left, 20.9);
  lens(right, 20.9);
  pc.lineF(left + 2.4, 20.4, right - 2.4, 20.4, frame, 0.6);
  // Arms back to the ears.
  pc.lineF(left - 2.6, 20.4, 7.4, 20, frame, 0.55);
  if (view === 'front') pc.lineF(right + 2.6, 20.4, 24.6, 20, frame, 0.55);
}

const GOLD_HAT = '#f0bf36';

function drawHat(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  const back = isBackView(view);
  const side = view === 'side';
  const x0 = side ? 9 : 8;
  const x1 = 24;
  const cx = (x0 + x1) / 2 + (side ? 0.5 : 0);

  switch (c.hat) {
    case HAT.beanie: {
      const wool = '#2c9f9b';
      pc.path(
        new PathBuilder().moveTo(x0 - 0.4, 12.4).curveTo(x0 - 1.2, 3.6, cx - 3, 3.2, cx, 3.2).curveTo(cx + 3, 3.2, x1 + 1.2, 3.6, x1 + 0.4, 12.4),
        across(wool, x0, x1, 1.14, 0.76),
      );
      for (let x = x0 + 1.4; x < x1 - 0.5; x += 2.6) pc.lineF(x, 5.2, x + 0.2, 11.2, shade(wool, 0.82), 0.45);
      pc.rrect(x0 - 0.8, 10.4, x1 - x0 + 1.6, 3.2, 1.5, between('#fbf7ec', '#dcd5c6'));
      pc.ellipse(cx, 3.2, 2.6, 2.2, between('#fbf7ec', '#dcd5c6', 60));
      return;
    }
    case HAT.straw: {
      const straw = '#e8c872';
      pc.ellipse(cx, 11.6, side ? 11.4 : 12.6, 2.9, between(straw, shade(straw, 0.8), 0));
      pc.path(
        new PathBuilder().moveTo(9.6, 11.6).curveTo(9.6, 3.6, 12.8, 3.4, 16, 3.4).curveTo(19.2, 3.4, 22.4, 3.6, 22.4, 11.6),
        across(straw, 9.6, 22.4, 1.1, 0.82),
      );
      pc.rrect(9.6, 8.6, 12.8, 2.2, 1, '#d9433b');
      return;
    }
    case HAT.chef: {
      const white = '#fbfaf5';
      for (const [x, y, r] of [[12, 5.4, 4], [16, 4, 4.6], [20.4, 5.4, 4]] as const) {
        pc.ellipse(x, y, r, r, between(white, '#e6e2d6', 30));
      }
      pc.rrect(9.2, 7.6, 13.6, 5.8, 1.6, across(white, 9, 23, 1.02, 0.86));
      return;
    }
    case HAT.hardHat: {
      const yellow = '#f0bf36';
      pc.path(
        new PathBuilder().moveTo(6.8, 12.4).curveTo(6.4, 3.4, 12, 3, 16, 3).curveTo(20, 3, 25.6, 3.4, 25.2, 12.4),
        across(yellow, 7, 25, 1.14, 0.76),
      );
      pc.rrect(14.6, 3.2, 2.8, 8.6, 1.2, '#ffd966');
      pc.rrect(side ? 8 : 5.6, 11, side ? 20 : 20.8, 2.4, 1.2, shade(yellow, 0.72));
      return;
    }
    case HAT.headphones: {
      const band = '#2a2a33';
      const cup = '#d9433b';
      if (side) {
        pc.path(new PathBuilder().ellipse(16, 15, 8.2, 9.6, Math.PI * 1.1, Math.PI * 1.9), '#00000000');
        pc.polyline([pt(9.6, 12), pt(10.4, 7.6), pt(16, 5.4), pt(21.4, 7.6)], band, 1.6);
        pc.ellipse(14.6, 21, 3, 4, between(shade(cup, 1.15), shade(cup, 0.75), 45));
      } else {
        pc.polyline([pt(7.4, 18), pt(7, 10), pt(11.6, 5.6), pt(16, 4.8), pt(20.4, 5.6), pt(25, 10), pt(24.6, 18)], band, 1.7);
        pc.rrect(4.4, 16, 3.8, 8, 1.8, between(shade(cup, 1.15), cup, 0));
        pc.rrect(23.8, 16, 3.8, 8, 1.8, between(cup, shade(cup, 0.72), 0));
      }
      return;
    }
    case HAT.party: {
      const x = side ? 15 : 15.4;
      pc.poly([pt(x, 0.6), pt(x + 5.6, 12), pt(x - 5.6, 12)], between('#f06aa8', '#c93a80', 0));
      pc.polyline([pt(x - 3.6, 8.6), pt(x + 3.8, 8), ], '#ffd166', 1.3);
      pc.polyline([pt(x - 4.6, 11), pt(x + 4.8, 10.4)], '#7fe0ff', 1.3);
      pc.ellipse(x, 1.4, 2, 2, '#fffaf0');
      return;
    }
    case HAT.topHat: {
      const felt = '#2a2630';
      pc.rrect(side ? 8 : 6, 10.8, side ? 17 : 20, 2.6, 1.3, between('#3a3540', felt));
      pc.rrect(10.8, 0.8, 10.8, 11, 1.4, across(felt, 11, 22, 1.4, 0.8));
      pc.rrect(10.8, 7.8, 10.8, 2.2, 0.4, '#d9433b');
      return;
    }
    case HAT.crown: {
      const gold = GOLD_HAT;
      pc.path(
        new PathBuilder()
          .moveTo(9.6, 12)
          .lineTo(9, 4.2)
          .lineTo(12.6, 7.4)
          .lineTo(16, 2.6)
          .lineTo(19.4, 7.4)
          .lineTo(23, 4.2)
          .lineTo(22.4, 12),
        across(gold, 9, 23, 1.2, 0.78),
      );
      pc.rrect(9.6, 10.4, 12.8, 2, 0.8, shade(gold, 0.72));
      pc.ellipse(16, 9, 0.9, 0.9, '#d9433b');
      pc.ellipse(12.6, 9, 0.7, 0.7, '#5bc8f0');
      pc.ellipse(19.4, 9, 0.7, 0.7, '#5bc8f0');
      pc.ellipse(16, 3.2, 0.8, 0.8, '#fff2b0');
      return;
    }
    case HAT.beret: {
      const wool = '#5a2a5e';
      pc.path(
        new PathBuilder()
          .moveTo(x0 - 0.6, 12)
          .curveTo(x0 - 1.4, 4.4, cx - 2, 2.6, cx + 1.4, 3)
          .curveTo(x1 + 1.4, 3.6, x1 + 1.2, 8, x1 + 0.4, 12)
          .quadTo(cx, 14, x0 - 0.6, 12),
        across(wool, x0, x1, 1.16, 0.76),
      );
      pc.rrect(x0 - 0.4, 10.6, x1 - x0 + 1.2, 2.6, 1.3, between(shade(wool, 0.66), shade(wool, 0.52)));
      pc.ellipse(cx + 3, 2.8, 1, 0.8, shade(wool, 0.55));
      return;
    }
    case HAT.bandana: {
      const cloth = '#d9433b';
      if (back) {
        pc.path(new PathBuilder().moveTo(x0 - 0.4, 14).lineTo(x1 + 0.4, 14).lineTo(cx, 20.4).lineTo(x0 - 0.4, 14), across(cloth, x0, x1, 1.1, 0.8));
        pc.ellipse(cx, 14.6, 1.6, 1, shade(cloth, 0.62));
      } else {
        pc.path(
          new PathBuilder()
            .moveTo(x0 - 0.4, 13.6)
            .curveTo(x0 - 1, 5.6, cx - 3, 4.2, cx, 4.2)
            .curveTo(cx + 3, 4.2, x1 + 1, 5.6, x1 + 0.4, 13.6)
            .lineTo(x0 - 0.4, 13.6),
          across(cloth, x0, x1, 1.14, 0.78),
        );
        pc.rrect(x0 - 0.6, 12, x1 - x0 + 1.2, 2, 1, shade(cloth, 0.7));
        if (view === 'front') pc.poly([pt(x1 - 1, 13.4), pt(x1 + 2, 17), pt(x1 - 0.2, 17)], shade(cloth, 0.85));
      }
      return;
    }
    case HAT.flower: {
      const [x, y] = side ? [11, 10.6] : back ? [10.4, 11.4] : [21.4, 11.4];
      for (let i = 0; i < 5; i += 1) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        pc.ellipse(x + Math.cos(a) * 1.7, y + Math.sin(a) * 1.7, 1.25, 1.25, between('#ffb3cf', '#ff8fb8', 45));
      }
      pc.ellipse(x, y, 1, 1, '#f0bf36');
      return;
    }
    case HAT.firecracker: {
      // A festive gold crown with hanging firecracker tassels; pairs with a flame effect.
      const gold = GOLD_HAT;
      const red = '#d9433b';
      pc.path(
        new PathBuilder().moveTo(x0 + 0.6, 12).lineTo(x0 + 3, 4.2).lineTo(cx, 7.6).lineTo(x1 - 3, 4.2).lineTo(x1 - 0.6, 12),
        across(gold, x0, x1, 1.2, 0.78),
      );
      pc.rrect(x0 + 0.4, 10.4, x1 - x0 - 0.8, 2.2, 1, shade(gold, 0.72));
      pc.ellipse(cx, 3.6, 0.9, 0.9, '#fff2b0');
      const tassel = (tx: number) => {
        pc.rrect(tx - 0.8, 12, 1.6, 4.6, 0.6, between(shade(red, 1.15), shade(red, 0.78)));
        pc.rrect(tx - 0.8, 12, 1.6, 1, 0.4, gold);
        pc.ellipse(tx, 16.8, 0.7, 0.6, gold);
      };
      tassel(x0 + 1.6);
      if (!side) tassel(x1 - 1.6);
      return;
    }
    default:
      // HAT.none, and HAT.cap, which is drawn as a hairstyle (see hairOf).
      return;
  }
}

/** A kopitiam cup: white body, a stripe of the drink showing at the rim. */
function drawCup(pc: PixelCanvas, x: number, y: number, drink: DrinkKind): void {
  pc.path(
    new PathBuilder().moveTo(x, y).lineTo(x + 5.4, y).lineTo(x + 4.6, y + 5.2).quadTo(x + 2.7, y + 6, x + 0.8, y + 5.2),
    across('#f7f3ea', x, x + 5.4, 1.02, 0.86),
  );
  pc.ellipse(x + 2.7, y + 0.3, 2.7, 0.8, DRINK_COLORS[drink]);
  pc.path(new PathBuilder().ellipse(x + 5.4, y + 2.2, 1.5, 1.3, -Math.PI / 2, Math.PI / 2), '#dcd5c6');
  pc.ellipse(x + 2.6, y + 3, 1, 0.8, '#3d9e8c');
}

/** A phone held up to the ear: dark case, lit screen, a bright edge. */
function drawPhone(pc: PixelCanvas, x: number, y: number): void {
  pc.rrect(x, y, 4.2, 7.4, 1.1, between('#4a5263', '#2a2f3a'));
  pc.rrect(x + 0.7, y + 0.9, 2.8, 4.8, 0.6, between('#a6ecff', '#5cc8ec'));
  pc.ellipse(x + 2.1, y + 6.6, 0.4, 0.4, '#ffffff');
}

/* -------------------------------------------------------------------------- */
/* Composition                                                                */
/* -------------------------------------------------------------------------- */

function armStates(pose: AvatarPose, view: AvatarView): { left: ArmState; right: ArmState } {
  const { action, frame } = pose;

  if (action === 'wave') {
    return { left: 'down', right: frame % 2 === 0 ? 'wave-a' : 'wave-b' };
  }
  if (action === 'dance') {
    const f = frame % 4;
    return {
      left: f === 0 || f === 1 ? 'up' : 'down',
      right: f === 2 || f === 1 ? 'up' : 'down',
    };
  }
  if (action === 'swim') {
    // An overhead freestyle stroke, one arm at a time.
    const f = frame % 4;
    return { left: f < 2 ? 'up' : 'down', right: f < 2 ? 'down' : 'up' };
  }

  let left: ArmState = 'down';
  let right: ArmState = 'down';
  if (action === 'walk' || action === 'treadWater') {
    const f = frame % 4;
    left = f === 1 ? 'swing-f' : f === 3 ? 'swing-b' : 'down';
    right = f === 1 ? 'swing-b' : f === 3 ? 'swing-f' : 'down';
  }
  if ((pose.carrying || pose.phone) && !isBackView(view)) {
    // The drink (or phone) hand is the one nearest the viewer; a phone is always at the ear.
    const raised = pose.phone || pose.sipping;
    if (view === 'side') right = raised ? 'sip' : 'hold';
    else left = raised ? 'sip' : 'hold';
  }
  return { left, right };
}

/** Overalls' bib and straps, worn over whatever top is underneath. */
function drawOveralls(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  const back = isBackView(view);
  const side = view === 'side';
  const shift = view === 'front3q' ? 1 : 0;
  const denim = c.pants;
  if (side) {
    pc.rrect(18.2, 33.4, 2.6, 10.6, 1, across(denim, 18.2, 20.8, 1.1, 0.85));
    pc.lineF(17.6, 30.4, 19, 33.6, shade(denim, 1.1), 1.2);
    return;
  }
  if (back) {
    pc.lineF(12.6, 30.4, 12.6, 40, denim, 1.4);
    pc.lineF(19.4, 30.4, 19.4, 40, shade(denim, 0.85), 1.4);
    return;
  }
  pc.rrect(11.6 + shift, 36.4, 9, 7.2, 1.6, across(denim, 11.6, 20.6, 1.1, 0.82));
  pc.lineF(12.8 + shift, 30.4, 13.4 + shift, 37, denim, 1.3);
  pc.lineF(19.2 + shift, 30.4, 18.6 + shift, 37, shade(denim, 0.9), 1.3);
  pc.rrect(14.6 + shift, 37.2, 1.6, 1.6, 0.5, shade(denim, 0.6));
}

function drawDressedTorso(pc: PixelCanvas, view: AvatarView, c: Colors): void {
  drawTorso(pc, view, c);
  if (c.print) drawPrint(pc, view, c.print);
  styleTop(pc, view, c);
  if (c.bottom === BOTTOM.overalls) drawOveralls(pc, view, c);
}

/** What a hand holds: a cup, a phone, or nothing. */
function drawHeld(pc: PixelCanvas, pose: AvatarPose, hand: Point, side: boolean): void {
  if (pose.phone) {
    drawPhone(pc, side ? hand.x - 1.4 : hand.x - 2.6, hand.y - 5);
    return;
  }
  if (!pose.carrying) return;
  if (pose.sipping) drawCup(pc, hand.x - 2.4, hand.y - 3, pose.carrying);
  else drawCup(pc, hand.x - 2.6, hand.y - 4.6, pose.carrying);
}

/** A lightsaber held in the hand: a small metal hilt and a glowing blade rising from it. */
function drawProp(pc: PixelCanvas, prop: number, hand: Point): void {
  const colors = PROP_COLOR[prop];
  if (!colors) return;
  const x = hand.x;
  const y = hand.y;
  pc.rrect(x - 0.9, y - 1.4, 1.8, 4, 0.6, across('#a8a8a8', x - 0.9, x + 0.9, 1.25, 0.8));
  pc.rrect(x - 0.65, y - 2, 1.3, 0.9, 0.35, '#dcd5c6');
  pc.rrect(x - 1.5, y - 13.4, 3, 12.4, 1.3, `${colors.glow}55`);
  pc.rrect(x - 0.75, y - 13, 1.5, 12, 0.6, across(colors.core, x - 0.75, x + 0.75, 1.35, 0.9));
  pc.rrect(x - 0.3, y - 12.6, 0.6, 11.2, 0.25, shade(colors.core, 1.6));
}

/**
 * Draws one avatar frame. Mirroring is applied last, so every view is authored
 * facing right.
 */
export function drawAvatar(look: Look, pose: AvatarPose): PixelCanvas {
  const pc = new PixelCanvas(AVATAR_WIDTH, AVATAR_HEIGHT);
  const c = colorsFor(look);
  const { view, mirror } = viewFor(pose.direction);
  const arms = armStates(pose, view);

  // Dancing bounces the whole upper body; swimming and treading water bob gently.
  const bob =
    pose.action === 'dance'
      ? (pose.frame % 2 === 1 ? -2 : 0)
      : pose.action === 'walk' && pose.frame % 2 === 1
        ? -1
        : (pose.action === 'swim' || pose.action === 'treadWater') && pose.frame % 2 === 1
          ? -1
          : 0;

  drawLegs(pc, view, pose.action, pose.frame, c);
  drawBottom(pc, view, pose.action, c);

  pc.oy = bob;
  if (view === 'side') {
    drawDressedTorso(pc, view, c);
    drawHead(pc, view, c, pose.blinking);
    drawHair(pc, view, c);
    drawGlasses(pc, view, c);
    drawHat(pc, view, c);
    const hand = drawArm(pc, armShape(view, 1, arms.right, false), c.sleeve, c.skin, false);
    drawHeld(pc, pose, hand, true);
    if (!pose.carrying && !pose.phone) drawProp(pc, c.prop, hand);
  } else if (isBackView(view)) {
    const narrow = view === 'back3q';
    drawArm(pc, armShape(view, -1, arms.left, false), c.sleeve, c.skin, false);
    drawArm(pc, armShape(view, 1, arms.right, narrow), c.sleeve, c.skin, true, narrow ? 3 : 3.5);
    drawDressedTorso(pc, view, c);
    drawHead(pc, view, c, pose.blinking);
    drawHair(pc, view, c);
    drawHat(pc, view, c);
  } else {
    drawDressedTorso(pc, view, c);
    const handL = drawArm(pc, armShape(view, -1, arms.left, false), c.sleeve, c.skin, false);
    drawArm(pc, armShape(view, 1, arms.right, view === 'front3q'), c.sleeve, c.skin, true, view === 'front3q' ? 3 : 3.5);
    drawHead(pc, view, c, pose.blinking);
    drawHair(pc, view, c);
    drawGlasses(pc, view, c);
    drawHat(pc, view, c);
    drawHeld(pc, pose, handL, false);
    if (!pose.carrying && !pose.phone) drawProp(pc, c.prop, handL);
  }
  pc.oy = 0;

  // In the pool, the body below the waterline is underwater: cut it away so
  // the tile's own water art shows through, with a pale ripple at the cut.
  if (pose.action === 'swim' || pose.action === 'treadWater') {
    pc.erase(0, AVATAR_WATERLINE, AVATAR_WIDTH, AVATAR_HEIGHT - AVATAR_WATERLINE);
    pc.rect(0, AVATAR_WATERLINE - 0.8, AVATAR_WIDTH, 1.6, '#e8f9ffa8');
  }

  pc.outline(OUTLINE_COLOR);
  if (mirror) pc.flipX();
  return pc;
}

/** Head-and-shoulders crop for chat bubbles, lists and the infostand. */
export function drawAvatarHead(look: Look): PixelCanvas {
  const full = drawAvatar(look, {
    direction: 3,
    action: 'stand',
    frame: 0,
    carrying: null,
    sipping: false,
    phone: false,
    blinking: false,
  });
  const head = new PixelCanvas(AVATAR_WIDTH, 34);
  head.blit(full, 0, -2);
  return head;
}

export function avatarKey(look: Look, pose: AvatarPose): string {
  return [
    lookKey(look),
    pose.direction,
    pose.action,
    pose.frame,
    pose.carrying ?? '-',
    pose.sipping ? 1 : 0,
    pose.phone ? 1 : 0,
    pose.blinking ? 1 : 0,
  ].join('.');
}
